import type { SelectedSettlement, SettlementListItem, SettlementPayout } from '../types';

export const CLAIMS_PROGRESS_STEP_USD = 5000;

type PayoutAmounts = {
  noProofUsd: number | null;
  withProofUsd?: number | null;
  maxUsd?: number | null;
};

/** iOS `Claim.payoutPotentialUSD`: max of max / no-proof / with-proof. All missing → unknown. */
export function payoutPotentialUsd(item: PayoutAmounts): number | null {
  const amounts = [item.maxUsd, item.noProofUsd, item.withProofUsd].filter(
    (n): n is number => n != null,
  );
  return amounts.length > 0 ? Math.max(...amounts) : null;
}

/** iOS `claimsSelectedPayoutGoal`: always the next $5k bucket, never equal to known. */
export function claimsProgressGoalUsd(total: number): number {
  const known = Math.max(0, total);
  return (Math.floor(known / CLAIMS_PROGRESS_STEP_USD) + 1) * CLAIMS_PROGRESS_STEP_USD;
}

/** Fill color from Figma: orange <50%, lime <75%, green through 100%. */
export function claimsProgressBarClass(ratio: number): string {
  if (ratio <= 0) return 'bg-transparent';
  if (ratio < 0.5) return 'bg-[#ffb21d]';
  if (ratio < 0.75) return 'bg-[#6bce3e]';
  return 'bg-mf-green';
}

export function formatUsd(amount: number): string {
  return `$${Math.round(amount).toLocaleString('en-US')}`;
}

/** iOS footer currency: 0–2 fraction digits. */
export function formatClaimsProgressUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** iOS `ClaimsViewModel.formatPayoutCurrency` — card “Claim up to” amount. */
export function formatClaimPayoutUsd(amount: number): string {
  const hasFractionalCents = Math.round(amount * 100) % 100 !== 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasFractionalCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function claimsProgressLabel(known: number, unknownCount: number, goal: number): string {
  const suffix = unknownCount > 0 ? '+' : '';
  return `${formatClaimsProgressUsd(known)}${suffix} / ${formatClaimsProgressUsd(goal)}`;
}

/** Card “Claim up to” amount: typical, else “up to” + max, else raw text. */
export function claimUpToAmount(payout: SettlementPayout): string {
  if (payout.typical_usd != null) return formatUsd(payout.typical_usd);
  if (payout.max_usd != null) return `up to ${formatUsd(payout.max_usd)}`;
  return payout.raw?.trim() || '—';
}

export function upToAmountLabel(item: SelectedSettlement): string {
  if (item.typicalUsd != null) return `Up to ${formatUsd(item.typicalUsd)}`;
  if (item.maxUsd != null) return `Up to ${formatUsd(item.maxUsd)}`;
  return item.payoutRaw?.trim() || item.payoutLabel || '';
}

export function sumPayoutPotentialUsd(items: PayoutAmounts[]): number {
  return items.reduce((sum, item) => sum + (payoutPotentialUsd(item) ?? 0), 0);
}

export function claimsPayoutUnknownCount(items: PayoutAmounts[]): number {
  return items.filter((item) => payoutPotentialUsd(item) == null).length;
}

/** Checkout total uses the same potential as the claims footer. */
export function sumNoProofUsd(items: PayoutAmounts[]): number {
  return sumPayoutPotentialUsd(items);
}

/** Company logo: `display.icon_url`, then top-level `icon_url` (iOS catalog mapping). */
export function settlementIconUrl(item: SettlementListItem): string | null {
  return item.display.icon_url ?? item.icon_url ?? null;
}

export function toSelectedSettlement(item: SettlementListItem): SelectedSettlement {
  return {
    id: item.id,
    company: item.display.company || item.name,
    iconUrl: settlementIconUrl(item),
    noProofUsd: item.payout.no_proof_usd,
    withProofUsd: item.payout.with_proof_usd ?? null,
    typicalUsd: item.payout.typical_usd,
    maxUsd: item.payout.max_usd,
    payoutRaw: item.payout.raw,
    payoutLabel: item.display.payout_label,
    deadlineLabel: item.display.deadline_label,
    badge: item.display.badge,
    proofRequired: item.proof_required,
    proofLabel: item.display.proof_label,
  };
}
